/**
 * PostHog `before_send` scrubber — the privacy gate for analytics.
 *
 * PCNshark's /dashboard renders customer BOM data (MPNs, manufacturer +
 * customer part numbers, ref designators, descriptions). PostHog would
 * otherwise ship that to a third party through several channels — element
 * text AND element attributes on autocapture clicks (the BOM table sets
 * `title={mpn}` etc.), the shareable `?filter=mpn:…` URL on $current_url
 * AND on the session-entry / $set_once URLs that persist for the whole
 * session. So this runs on EVERY event and enforces, deny-by-default:
 *
 *   - /staff/*      → DROP entirely. Internal tooling, no analytics.
 *   - /dashboard/*  → DROP autocapture entirely (element text + every
 *                     attr__* is a leak vector; masking field-by-field is
 *                     whack-a-mole). Pageviews, pageleaves, custom events,
 *                     and identify still flow.
 *   - any URL-valued property pointing at /dashboard or /staff → strip its
 *     query string + hash, regardless of which page the event fired on
 *     (the session-entry + $set_once URLs persist onto later marketing
 *     events). Marketing URLs keep their query so UTM / referral
 *     attribution survives.
 *
 * Pure + window-free so it can be unit-tested; the caller passes the live
 * `window.location.pathname` as the fallback for events with no own path.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Bag = Record<string, any>;
type PostHogEventLike = {
  event?: string;
  properties?: Bag;
  // $set / $set_once are siblings of `properties` on the event envelope,
  // not inside it — they carry person props derived from the entry URL.
  $set?: Bag;
  $set_once?: Bag;
} | null;

// Events that carry an autocapture DOM payload (element text + attrs).
const AUTOCAPTURE_EVENTS = new Set([
  "$autocapture",
  "$rageclick",
  "$dead_click",
  "$copy_autocapture",
]);

// Every URL-valued property PostHog attaches across the event envelope +
// person property bags. Pathnames carry no query but are listed for safety.
const URL_PROPS = [
  "$current_url",
  "$referrer",
  "$pathname",
  "$session_entry_url",
  "$session_entry_pathname",
  "$session_entry_referrer",
  "$initial_current_url",
  "$initial_pathname",
  "$initial_referrer",
];

/** Path portion only, query/hash dropped. Accepts a full URL or a bare path. */
function pathOf(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;
  try {
    return new URL(value).pathname;
  } catch {
    return value.startsWith("/") ? value.split(/[?#]/)[0] : null;
  }
}

function stripQuery(value: unknown): unknown {
  if (typeof value !== "string") return value;
  return value.split(/[?#]/)[0];
}

function isSensitivePath(path: string | null): boolean {
  return !!path && (path.startsWith("/dashboard") || path.startsWith("/staff"));
}

/** Strip query/hash from a URL ONLY if it points at a sensitive area. */
function scrubUrlProp(value: unknown): unknown {
  return isSensitivePath(pathOf(value)) ? stripQuery(value) : value;
}

const URL_KEYS = new Set(URL_PROPS);

/**
 * Recursively scrub every URL-valued property (by its own path) anywhere in a
 * bag — top-level AND nested. PostHog buries URLs inside composite events
 * (e.g. `$web_vitals_LCP_event.$current_url`), so a flat top-level pass would
 * miss them. Depth-limited; only rewrites values whose KEY is a known URL prop
 * (everything else is left untouched, so marketing autocapture survives).
 */
function scrubUrlsDeep(value: unknown, depth = 0): void {
  if (depth > 6 || value == null || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const item of value) scrubUrlsDeep(item, depth + 1);
    return;
  }
  const obj = value as Bag;
  for (const key of Object.keys(obj)) {
    if (URL_KEYS.has(key)) {
      obj[key] = scrubUrlProp(obj[key]);
    } else {
      scrubUrlsDeep(obj[key], depth + 1);
    }
  }
}

function isAutocapture(event: PostHogEventLike, props: Bag): boolean {
  return (
    (!!event?.event && AUTOCAPTURE_EVENTS.has(event.event)) ||
    props.$elements != null ||
    props.$elements_chain != null ||
    props.$el_text != null
  );
}

export function scrubPostHogEvent(
  event: PostHogEventLike,
  fallbackPath?: string,
): PostHogEventLike {
  if (!event) return event;
  const props = event.properties ?? {};

  const path =
    pathOf(props.$pathname) ??
    pathOf(props.$current_url) ??
    (fallbackPath ? pathOf(fallbackPath) : null) ??
    "";

  // Internal staff tooling: no analytics, ever.
  if (path.startsWith("/staff")) return null;

  // Customer-data surface: drop autocapture wholesale (its element text +
  // attributes would carry BOM identifiers). Pageviews / pageleaves /
  // custom events / identify are unaffected.
  if (path.startsWith("/dashboard") && isAutocapture(event, props)) {
    return null;
  }

  // Strip query/hash from any URL prop pointing at a sensitive area — on
  // EVERY event, since the session-entry + $set_once URLs persist across the
  // session (and ride along on later marketing-page events), and some events
  // (e.g. $web_vitals) bury a $current_url several levels deep.
  scrubUrlsDeep(props);
  scrubUrlsDeep(event.$set);
  scrubUrlsDeep(event.$set_once);

  event.properties = props;
  return event;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
